import {
  Bot,
  Building2,
  ChevronRight,
  Database,
  Laptop2,
  FolderKanban,
  ImageIcon,
  LayoutDashboard,
  Search,
  ServerCog,
  Settings,
  Share2,
  Shield,
  Trash2,
  Users,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAgentStore } from "@/store/agentStore";
import { useAuthStore } from "@/store/authStore";
import { useUiStore } from "@/store/uiStore";

interface SidebarProps {
  variant: "user" | "admin";
}

const userNav = [
  { href: "/app/files", label: "My Drive", icon: FolderKanban },
  { href: "/app/home", label: "Dashboard", icon: LayoutDashboard },
  { href: "/app/devices", label: "Devices", icon: Laptop2 },
  { href: "/app/photos", label: "Photos", icon: ImageIcon },
  { href: "/app/shared", label: "Shared", icon: Share2 },
  { href: "/app/search", label: "Search", icon: Search },
  { href: "/app/trash", label: "Trash", icon: Trash2 },
  { href: "/app/settings", label: "Settings", icon: Settings },
];

const adminNav = [
  { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/tenants", label: "Tenants", icon: Building2 },
  { href: "/admin/users", label: "Users", icon: Users },
  { href: "/admin/storage", label: "Storage", icon: Database },
  { href: "/admin/audit", label: "Audit", icon: Shield },
  { href: "/admin/system", label: "System", icon: ServerCog },
  { href: "/admin/settings", label: "Settings", icon: Settings },
];

export function Sidebar({ variant }: SidebarProps) {
  const location = useLocation();
  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const openAgentPanel = useAgentStore((state) => state.openPanel);
  const tenant = useAuthStore((state) => state.tenant);
  const navItems = variant === "admin" ? adminNav : userNav;
  const primaryItems = navItems.slice(0, 4);
  const secondaryItems = navItems.slice(4);

  return (
    <aside
      className={cn(
        "hidden border-r border-border-subtle/80 bg-background-secondary/40 backdrop-blur-xl transition-all duration-200 ease-smooth lg:flex lg:flex-col",
        collapsed ? "lg:w-[86px]" : "lg:w-[288px]",
      )}
    >
      <div className="flex h-16 items-center gap-3 border-b border-border-subtle/80 px-5">
        <div className="shrink-0 rounded-lg border border-border/70 bg-background-secondary/70 shadow-md overflow-hidden">
          <img
            alt="seaDrive"
            className="h-10 w-10 object-cover"
            src="/logo.svg"
          />
        </div>
        {!collapsed ? (
          <div className="min-w-0 animate-fade-in">
            <p className="truncate text-base font-bold tracking-tight text-text-primary">
              <span>Sea</span>
              <span className="text-accent">Drive</span>
            </p>
            <p className="truncate text-xs text-text-tertiary">{tenant?.name ?? "Workspace"}</p>
          </div>
        ) : null}
      </div>

      <nav className="flex-1 space-y-6 px-3 py-5">
        {variant === "user" ? (
          <div className="space-y-1.5">
            {!collapsed ? (
              <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-tertiary">
                Assistant
              </p>
            ) : null}
            <Button
              className={cn(
                "group relative flex w-full items-center gap-3 overflow-hidden rounded-2xl px-3.5 py-3 text-sm transition-all duration-150 ease-smooth",
                collapsed && "justify-center px-0",
              )}
              title={collapsed ? "AI Assistant" : undefined}
              type="button"
              variant="ghost"
              onClick={openAgentPanel}
            >
              <Bot className="h-4.5 w-4.5 shrink-0 text-text-tertiary group-hover:text-text-primary" />
              {!collapsed ? <span className="truncate font-medium text-text-secondary group-hover:text-text-primary">AI Assistant</span> : null}
            </Button>
          </div>
        ) : null}

        <div className="space-y-1.5">
          {!collapsed ? (
            <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-tertiary">
              Workspace
            </p>
          ) : null}
          {primaryItems.map((item) => {
            const active =
              location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
            const Icon = item.icon;

            return (
              <Link
                key={item.href}
                title={collapsed ? item.label : undefined}
                to={item.href}
                className={cn(
                  "group relative flex items-center gap-3 overflow-hidden rounded-2xl px-3.5 py-3 text-sm transition-all duration-150 ease-smooth",
                  active
                    ? "bg-accent-subtle/90 text-text-primary shadow-sm"
                    : "text-text-secondary hover:bg-background-elevated/80 hover:text-text-primary",
                  collapsed && "justify-center px-0",
                )}
              >
                {active ? (
                  <span className="absolute left-0 top-1/2 h-9 w-[3px] -translate-y-1/2 rounded-r-full bg-accent" />
                ) : null}
                <Icon
                  className={cn(
                    "h-4.5 w-4.5 shrink-0 transition-colors duration-150",
                    active ? "text-accent" : "text-text-tertiary group-hover:text-text-primary",
                  )}
                />
                {!collapsed ? (
                  <>
                    <span className="truncate font-medium">{item.label}</span>
                    {active ? <ChevronRight className="ml-auto h-4 w-4 text-accent/70" /> : null}
                  </>
                ) : null}
              </Link>
            );
          })}
        </div>

        <div className="space-y-1.5">
          {!collapsed ? (
            <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-tertiary">
              Library
            </p>
          ) : null}
          {secondaryItems.map((item) => {
            const active =
              location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
            const Icon = item.icon;

            return (
              <Link
                key={item.href}
                title={collapsed ? item.label : undefined}
                to={item.href}
                className={cn(
                  "group relative flex items-center gap-3 overflow-hidden rounded-2xl px-3.5 py-3 text-sm transition-all duration-150 ease-smooth",
                  active
                    ? "bg-accent-subtle/90 text-text-primary shadow-sm"
                    : "text-text-secondary hover:bg-background-elevated/80 hover:text-text-primary",
                  collapsed && "justify-center px-0",
                )}
              >
                {active ? (
                  <span className="absolute left-0 top-1/2 h-9 w-[3px] -translate-y-1/2 rounded-r-full bg-accent" />
                ) : null}
                <Icon
                  className={cn(
                    "h-4.5 w-4.5 shrink-0 transition-colors duration-150",
                    active ? "text-accent" : "text-text-tertiary group-hover:text-text-primary",
                  )}
                />
                {!collapsed ? <span className="truncate font-medium">{item.label}</span> : null}
              </Link>
            );
          })}
        </div>
      </nav>

      {!collapsed && variant === "user" ? (
        <div className="border-t border-border-subtle/80 p-4">
          <div className="rounded-[24px] border border-border/70 bg-background-elevated/85 p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-text-tertiary">Storage</p>
              <span className="rounded-full bg-accent-subtle px-1.5 py-0.5 text-[10px] font-semibold text-accent">
                64%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-background-tertiary">
              <div className="h-full rounded-full gradient-accent transition-all duration-500" style={{ width: "64%" }} />
            </div>
            <div className="mt-3 space-y-1">
              <p className="text-sm font-semibold text-text-primary">3.2 GB of 5 GB used</p>
              <p className="text-xs text-text-tertiary">
                Space is healthy, but a larger plan keeps backups and team uploads comfortable.
              </p>
            </div>
            <Button className="mt-4 h-9 w-full rounded-xl text-xs font-semibold" size="sm" variant="secondary">
              Manage storage
            </Button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
